import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLogo } from '../../../services/apiService';

const NineWiket = () => {
  const navigate = useNavigate();
  const apiMode = 'transfer';

  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const countdownRef = useRef(null);
  const logoRef = useRef(null);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  // Fetch site logo (uses centralized fetchLogo to deduplicate)
  useEffect(() => {
    fetchLogo()
      .then((data) => {
        const logoVal = data[0]?.logo || null;
        logoRef.current = logoVal;
        setLogo(logoVal);
      })
      .catch((error) => {
        console.error('Error fetching logo:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // 9WKT join + auto-retry on rate limit
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const remote_id = localStorage.getItem('remote_id');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/9wkt/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: 'eg_' + remote_id,
          token: token,
          providerSitePage: window.location.origin,
          origin: window.location.origin,
          siteLogo: `${process.env.REACT_APP_API_URL}/storage/${logoRef.current}`,
        }),
      });

      const dataly = await response.json();

      // Server returned an error message (may be HTTP 200 or 4xx)
      if (!response.ok || (dataly?.message && !dataly?.data && !dataly?.game_url)) {
        const msg = dataly?.message || 'Failed to launch game. Please try again.';
        const waitSeconds = typeof dataly?.seconds === 'number'
          ? dataly.seconds
          : typeof dataly?.second === 'number'
            ? dataly.second
            : null;
        setAlertMsg(msg);

        if (waitSeconds && waitSeconds > 0) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          // +10s safety buffer in case the server clock is slightly behind
          setCountdown(waitSeconds + 10);
          countdownRef.current = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
                setAlertMsg(null);
                setCountdown(null);
                fetchData(); // auto-relaunch
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
        return;
      }

      const iframeUrl = dataly?.data?.launch?.data?.launchUrl ?? dataly?.game_url;
      if (!iframeUrl) throw new Error('Invalid iframe launch response');

      if (iframeUrl.match(/mcwbd/i) || dataly?.data?.fullRedir || iframeUrl.includes('#/9wkt')) {
        setRedirecting(true);
        window.location.href = iframeUrl;
        return;
      }

      navigate('/gamerun', { state: { iframeUrl, apiMode } });
    } catch (error) {
      console.error('Error fetching player login:', error);
      setAlertMsg('Failed to launch game. Please try again.');
    }
  }, [navigate]);

  useEffect(() => {
    if (loading || !logo) return;
    fetchData();
  }, [logo, loading, fetchData]);

  return redirecting ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: '#0a0a0a', zIndex: 999999,
    }}>
      <div style={{
        width: '48px', height: '48px', border: '4px solid #e8ce78',
        borderTopColor: 'transparent', borderRadius: '50%',
        animation: 'nw-spin 0.8s linear infinite',
      }} />
      <p style={{ color: '#e8ce78', marginTop: '16px', fontSize: '15px' }}>Redirecting…</p>
      <style>{`@keyframes nw-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  ) : alertMsg ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', zIndex: 999999,
    }}>
      <div style={{
        background: '#1a1a2e', color: '#fff', padding: '28px 32px', borderRadius: '12px',
        textAlign: 'center', maxWidth: '320px', boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
      }}>
        <p style={{ marginBottom: '12px', fontSize: '15px' }}>{alertMsg}</p>
        {countdown !== null && countdown > 0 ? (
          <p style={{ fontSize: '13px', color: '#e8ce78', margin: 0 }}>
            Retrying in {countdown}s…
          </p>
        ) : (
          <button
            onClick={() => { setAlertMsg(null); fetchData(); }}
            style={{
              background: '#e8ce78', color: '#1a1a2e', border: 'none',
              padding: '10px 28px', borderRadius: '8px', fontWeight: 'bold',
              fontSize: '14px', cursor: 'pointer',
            }}
          >Retry</button>
        )}
      </div>
    </div>
  ) : null;
};

export default NineWiket;
